import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  FormControlLabel,
  Menu,
  Select,
  Collapse,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  Typography,
  GlobalStyles,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import SearchIcon from "@mui/icons-material/Search";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import useFetchObjects from "../../api/useFetchObjects";
import { useAuth } from "../../context/AuthContext";
import { useTranslation } from "react-i18next";

const apiBase = process.env.REACT_APP_API_BASE_URL;

const num = (value) => Number(value) || 0;
const createEmptyTransferItem = () => ({
  product: "",
  currency: "",
  transfer_quantity: "",
  buy_price: "",
  buy_price_touched: false,
  wholesale_price: "",
  wholesale_price_touched: false,
  retail_price: "",
  retail_price_touched: false,
  sale_price: "",
  sale_price_touched: false,
});

const StockTransferIndex = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const selectedBranchId = auth?.user?.selected_branch || "";
  const [openForm, setOpenForm] = useState(false);
  const [openDetails, setOpenDetails] = useState(false);
  const [openTransferConfirm, setOpenTransferConfirm] = useState(false);
  const [openPayment, setOpenPayment] = useState(false);
  const [selectedTransfer, setSelectedTransfer] = useState(null);
  const [selectedTransferGroup, setSelectedTransferGroup] = useState(null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [paymentData, setPaymentData] = useState(null);
  const [dialogMode, setDialogMode] = useState("view");
  const [message, setMessage] = useState(null);
  const [showPaymentBranch, setShowPaymentBranch] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState({
    search: "",
    product: "",
    supplier_branch: "",
    customer_branch: "",
    start_date: "",
    end_date: "",
  });
  const [form, setForm] = useState({
    product: "",
    supplier_branch: selectedBranchId,
    customer_branch: "",
    transfer_quantity: "",
    transfer_date: new Date().toISOString().slice(0, 10),
    notes: "",
  });
  const [transferItems, setTransferItems] = useState([createEmptyTransferItem()]);
  const [pendingTransfer, setPendingTransfer] = useState(null);
  const [transferPaymentAccountId, setTransferPaymentAccountId] = useState("");
  const [transferExchangeRate, setTransferExchangeRate] = useState("");
  const [transferDiscountAmount, setTransferDiscountAmount] = useState("");
  const [transferReceivedAmount, setTransferReceivedAmount] = useState("");
  const [paymentAccountId, setPaymentAccountId] = useState("");
  const [paymentAmount, setPaymentAmount] = useState("");
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [paymentDescription, setPaymentDescription] = useState("");
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentOverrides, setPaymentOverrides] = useState({});
  const [deletingTransferGroupKey, setDeletingTransferGroupKey] = useState("");
  const [actionMenuAnchorEl, setActionMenuAnchorEl] = useState(null);
  const [actionMenuGroup, setActionMenuGroup] = useState(null);

  const { data: branchesData } = useFetchObjects(
    ["stockTransferBranches"],
    "shop/shop/?page_size=all&include_all=1",
    token
  );
  const { data: stockData } = useFetchObjects(["stockTransferStockRows"], "shop/stocks/?page_size=all", token);
  const { data: accountsData } = useFetchObjects(["stock-transfer-payment-accounts"], "shop/accounts/?page_size=all", token);
  const {
    data: accountTransactionsData,
    refetch: refetchAccountTransactions,
  } = useFetchObjects(
    ["stock-transfer-payment-history"],
    "shop/account-transactions/?page_size=all",
    token
  );

  const query = useMemo(() => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.append(key, value);
    });
    params.append("page_size", "all");
    return `shop/stock-transfers/?${params.toString()}`;
  }, [filters]);

  const { data, isLoading, refetch } = useFetchObjects(["stockTransfers", query], query, token);

  const isSuperAdmin = useMemo(
    () =>
      Boolean(
        auth?.user?.user?.is_superuser ||
          auth?.user?.is_superuser
      ),
    [auth]
  );

  const isAdmin = useMemo(
    () =>
      Boolean(
        isSuperAdmin ||
          auth?.user?.user?.is_staff ||
          auth?.user?.is_staff
      ),
    [isSuperAdmin, auth]
  );

  const branches = branchesData?.results || branchesData || [];
  const stockRows = stockData?.results || stockData || [];
  const accounts = accountsData?.results || accountsData || [];
  const accountTransactions = accountTransactionsData?.results || accountTransactionsData || [];
  const transfers = data?.results || data || [];
  const safeTransfers = useMemo(() => {
    const all = transfers.filter(Boolean);
    if (isAdmin) return all;
    return all.filter(
      (row) =>
        String(row.supplier_branch) === String(selectedBranchId) ||
        String(row.customer_branch) === String(selectedBranchId)
    );
  }, [transfers, isAdmin, selectedBranchId]);

  useEffect(() => {
    setPage(0);
  }, [filters, rowsPerPage]);
  const branchOptions = useMemo(
    () => branches.map((branch) => String(branch.id)),
    [branches]
  );
  const branchNameById = useMemo(() => {
    const map = new Map();
    branches.forEach((branch) => {
      map.set(String(branch.id), branch.name);
    });
    return map;
  }, [branches]);
  const resolvedSelectedBranchId = branchOptions.includes(String(selectedBranchId))
    ? String(selectedBranchId)
    : "";
  const safeSupplierBranch = branchOptions.includes(String(form.supplier_branch))
    ? String(form.supplier_branch)
    : "";
  const safeCustomerBranch = branchOptions.includes(String(form.customer_branch))
    ? String(form.customer_branch)
    : "";
  const customerBranchOptions = useMemo(
    () => branches.filter((branch) => String(branch.id) !== String(form.supplier_branch)),
    [branches, form.supplier_branch]
  );
  const supplierBranchLabel = t("stock.fromBranch", "From Province");
  const customerBranchLabel = t("stock.toBranch", "To Province");
  const selectedCustomerBranch = useMemo(
    () => branches.find((branch) => String(branch.id) === String(safeCustomerBranch)) || null,
    [branches, safeCustomerBranch]
  );

  const activeBranchId = safeSupplierBranch || resolvedSelectedBranchId;
  const { data: currenciesData, loading: currencyLoading } = useFetchObjects(
    ["stockTransferCurrencies"],
    "shop/currencies/?page_size=all",
    token
  );
  const currencies = currenciesData?.results || currenciesData || [];
  const { data: branchStockData } = useFetchObjects(
    ["stockTransferBranchStocks", activeBranchId],
    "shop/stocks/?page_size=all",
    token
  );
  const branchStockRows = branchStockData?.results || branchStockData || [];

  const selectedProductStocks = useFetchObjects(
    ["selectedProductStocks", form.product, activeBranchId],
    form.product
      ? `shop/stocks/?purchase_item__product=${form.product}${
          activeBranchId ? `&shop=${activeBranchId}` : ""
        }&page_size=all`
      : null,
    token
  );
  const productStocks = selectedProductStocks?.data?.results || selectedProductStocks?.data || [];

  const availableProducts = useMemo(() => {
    const rowMap = new Map();
    [...(Array.isArray(stockRows) ? stockRows : []), ...(Array.isArray(branchStockRows) ? branchStockRows : [])].forEach((row) => {
      if (row?.id) rowMap.set(String(row.id), row);
    });
    const rows = Array.from(rowMap.values());
    const map = new Map();
    rows.forEach((row) => {
      // Filter by supplier branch if selected
      if (form.supplier_branch && String(row?.shop_details?.id) !== String(form.supplier_branch)) return;

      const product = row?.purchase_item_details?.product;
      const quantity = num(row?.quantity);
      if (!product?.id || quantity <= 0) return;
      if (product?.id && !map.has(String(product.id))) {
        map.set(String(product.id), {
          id: product.id,
          name: product.name,
          code: product.code,
        });
      }
    });
    return Array.from(map.values());
  }, [stockRows, branchStockRows, form.supplier_branch]);
  const allProducts = useMemo(() => {
    const rows = Array.isArray(stockRows) ? stockRows : [];
    const map = new Map();
    rows.forEach((row) => {
      const product = row?.purchase_item_details?.product;
      if (!product?.id) return;
      if (!map.has(String(product.id))) {
        map.set(String(product.id), {
          id: product.id,
          name: product.name,
          code: product.code,
        });
      }
    });
    return Array.from(map.values());
  }, [stockRows]);
  const getProductOptionsForItem = (item) => {
    const currentProductId = String(item?.product || "");
    const options = [...availableProducts];
    if (currentProductId && !options.some((product) => String(product.id) === currentProductId)) {
      const currentProductName =
        stockRows.find(
          (row) => String(row?.purchase_item_details?.product?.id) === currentProductId
        )?.purchase_item_details?.product?.name ||
        item?.product_details?.name ||
        `Product ${currentProductId}`;
      options.unshift({
        id: currentProductId,
        name: currentProductName,
        code: "",
      });
    }
    return options;
  };
  const getCurrencyTitleById = (currencyId) =>
    currencies.find((currency) => String(currency.id) === String(currencyId))?.title || "";
  const getDefaultCurrencyIdForProduct = (productId) => {
    const row =
      stockRows.find(
        (stock) => String(stock?.purchase_item_details?.product?.id) === String(productId)
      ) || null;
    return String(row?.currency_details?.id || row?.currency || "");
  };
  const getItemCurrencyId = (item) => {
    const directCurrencyId = String(
      item?.currency_details?.id ||
      item?.currency ||
      item?.source_stock_details?.currency_details?.id ||
      item?.source_stock_details?.currency?.id ||
      item?.source_stock_details?.currency ||
      item?.purchase_item_details?.currency_details?.id ||
      item?.purchase_item_details?.currency?.id ||
      item?.purchase_item_details?.currency ||
      ""
    );
    if (directCurrencyId) return directCurrencyId;
    return getDefaultCurrencyIdForProduct(item?.product || item?.product_details?.id || "");
  };
  const getItemCurrencyTitle = (item) =>
    item?.currency_details?.title ||
    item?.currency_details?.code ||
    item?.source_stock_details?.currency_details?.title ||
    item?.source_stock_details?.currency_details?.code ||
    item?.purchase_item_details?.currency_details?.title ||
    item?.purchase_item_details?.currency_details?.code ||
    getCurrencyTitleById(getItemCurrencyId(item));
  const getAllProductOptionsForItem = (item) => {
    const currentProductId = String(item?.product || "");
    const options = [...availableProducts];
    if (currentProductId && !options.some((product) => String(product.id) === currentProductId)) {
      const currentProductName = getTransferItemDisplayName(item);
      options.unshift({
        id: currentProductId,
        name: currentProductName,
        code: "",
      });
    }
    return options;
  };
  const availableProductIds = useMemo(
    () => availableProducts.map((product) => String(product.id)),
    [availableProducts]
  );
  const safeProduct = availableProductIds.includes(String(form.product))
    ? String(form.product)
    : "";
  const selectedProductStockRow = useMemo(() => {
    if (!safeProduct) return null;
    const branchId = activeBranchId;
    return (
      stockRows.find(
        (row) =>
          String(row?.purchase_item_details?.product?.id) === String(safeProduct) &&
          (!branchId || String(row?.shop_details?.id) === String(branchId))
      ) ||
      stockRows.find(
        (row) => String(row?.purchase_item_details?.product?.id) === String(safeProduct)
      ) ||
      null
    );
  }, [stockRows, safeProduct, activeBranchId]);
  const getTransferItemDisplayName = (item) =>
    item?.product_details?.name ||
    item?.source_stock_details?.product?.name ||
    item?.purchase_item_details?.product?.name ||
    stockRows.find((row) => String(row?.purchase_item_details?.product?.id) === String(item?.product))
      ?.purchase_item_details?.product?.name ||
    `Product ${item?.product || ""}`.trim();

  const getTransferItemSourcePrices = (item) => ({
    buy_price:
      item?.buy_price ??
      item?.sale_price ??
      item?.source_stock_details?.buy_price ??
      item?.purchase_item_details?.buy_price ??
      "",
    wholesale_price:
      item?.wholesale_price ??
      item?.source_stock_details?.wholesale_price ??
      item?.purchase_item_details?.wholesale_price ??
      "",
    retail_price:
      item?.retail_price ??
      item?.source_stock_details?.retail_price ??
      item?.source_stock_details?.sale_price ??
      item?.purchase_item_details?.retail_price ??
      item?.purchase_item_details?.sale_price ??
      "",
    sale_price:
      item?.sale_price ??
      item?.source_stock_details?.sale_price ??
      item?.source_stock_details?.retail_price ??
      item?.purchase_item_details?.sale_price ??
      item?.purchase_item_details?.retail_price ??
      "",
  });

  const getTransferItemEffectivePrice = (item) =>
    item?.buy_price ??
    item?.sale_price ??
    item?.source_stock_details?.buy_price ??
    item?.purchase_item_details?.buy_price ??
    item?.retail_price ??
    item?.source_stock_details?.sale_price ??
    item?.source_stock_details?.retail_price ??
    item?.purchase_item_details?.sale_price ??
    item?.purchase_item_details?.retail_price ??
    "";

  const getItemPriceValues = (productId) => {
    const currentBranchId = String(form.supplier_branch || activeBranchId || "");
    const productStockRow =
      stockRows.find(
        (row) =>
          String(row?.purchase_item_details?.product?.id) === String(productId) &&
          (!currentBranchId || String(row?.shop_details?.id) === currentBranchId)
      ) ||
      (!currentBranchId
        ? stockRows.find(
            (row) => String(row?.purchase_item_details?.product?.id) === String(productId)
          )
        : null) ||
      null;
    return {
      buy_price: productStockRow?.purchase_item_details?.buy_price ?? productStockRow?.buy_price ?? "",
      wholesale_price:
        productStockRow?.purchase_item_details?.wholesale_price ??
        productStockRow?.wholesale_price ??
        "",
      retail_price:
        productStockRow?.purchase_item_details?.retail_price ??
        productStockRow?.purchase_item_details?.sale_price ??
        productStockRow?.retail_price ??
        productStockRow?.sale_price ??
        "",
      sale_price:
        productStockRow?.purchase_item_details?.sale_price ??
        productStockRow?.sale_price ??
        productStockRow?.purchase_item_details?.retail_price ??
        productStockRow?.retail_price ??
        "",
    };
  };

  const getTransferItemPriceValue = (item, field) => {
    const fallbackPrices = {
      ...getTransferItemSourcePrices(item),
      ...getItemPriceValues(item?.product),
    };
    const rawValue = item?.[field];
    if (item?.[`${field}_touched`]) return rawValue ?? "";
    if (rawValue !== undefined && rawValue !== null && rawValue !== "") return rawValue;
    if (field === "sale_price") {
      return (
        item?.retail_price ??
        fallbackPrices.retail_price ??
        item?.wholesale_price ??
        fallbackPrices.wholesale_price ??
        fallbackPrices.sale_price ??
        ""
      );
    }
    return fallbackPrices[field] ?? "";
  };

  const getAvailableQuantityForItem = (item, index, isEditMode = false) => {
    const productId = String(item?.product || "");
    const supplierBranchId = String(form.supplier_branch || "");
    if (!productId) return "";

    let availableQuantity = stockRows
      .filter(
        (row) =>
          String(row?.purchase_item_details?.product?.id) === productId &&
          (!supplierBranchId || String(row?.shop_details?.id) === supplierBranchId)
      )
      .reduce((sum, row) => sum + num(row?.quantity), 0);

    if (isEditMode) {
      const originalItem =
        (item?.id
          ? selectedTransferGroup?.items?.find((row) => String(row?.id) === String(item.id))
          : null) || selectedTransferGroup?.items?.[index];
      const originalProductId = String(
        originalItem?.product_details?.id || originalItem?.product || ""
      );
      const originalSupplierBranchId = String(originalItem?.supplier_branch || "");
      const originalStatus = String(originalItem?.status || "").toLowerCase();

      if (
        originalStatus !== "rejected" &&
        productId === originalProductId &&
        supplierBranchId === originalSupplierBranchId
      ) {
        // Existing pending/accepted transfers already reduced source stock.
        // Add the original quantity back while editing so users can keep or increase it correctly.
        availableQuantity += num(originalItem?.transfer_quantity);
      }
    }

    return availableQuantity;
  };

  const handleTransferItemChange = (index, field, value) => {
    setTransferItems((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index
          ? {
              ...row,
              [field]: value,
              ...(field === "buy_price" ||
              field === "wholesale_price" ||
              field === "retail_price" ||
              field === "sale_price"
                ? { [`${field}_touched`]: true }
                : {}),
            }
          : row
      )
    );
  };

  const getTransferItemSalePrice = (item) => {
    const directPrice = Number(getTransferItemEffectivePrice(item) || 0);
    if (Number.isFinite(directPrice) && directPrice > 0) return directPrice;

    const stockRow =
      stockRows.find(
        (row) =>
          String(row?.purchase_item_details?.product?.id) ===
            String(item?.product?.id || item?.product_details?.id || item?.product || "") ||
          String(row?.purchase_item_details?.product?.id) ===
            String(item?.purchase_item_details?.product?.id || "")
      ) ||
      null;
    const fallbackPrice = Number(
      stockRow?.purchase_item_details?.buy_price ??
        stockRow?.buy_price ??
        stockRow?.purchase_item_details?.retail_price ??
        stockRow?.purchase_item_details?.sale_price ??
        stockRow?.source_stock_details?.buy_price ??
        stockRow?.source_stock_details?.retail_price ??
        stockRow?.source_stock_details?.sale_price ??
        stockRow?.sale_price ??
        stockRow?.retail_price ??
        0
    );
    return Number.isFinite(fallbackPrice) ? fallbackPrice : 0;
  };

  useEffect(() => {
    if (!message) return undefined;
    const timer = setTimeout(() => setMessage(null), 3000);
    return () => clearTimeout(timer);
  }, [message]);

  const currentStock = useMemo(() => {
    return num(selectedProductStockRow?.quantity);
  }, [selectedProductStockRow]);

  useEffect(() => {
    if (!selectedProductStockRow) return;
    const stockBranchId = String(selectedProductStockRow?.shop_details?.id || "");
    if (!stockBranchId) return;
    setForm((prev) => {
      if (String(prev.supplier_branch || "") === stockBranchId) return prev;
      return {
        ...prev,
        supplier_branch: stockBranchId,
      };
    });
  }, [selectedProductStockRow]);

  const acceptTransferGroup = (group) => {
    const pendingItems = (group?.items || []).filter(
      (item) => String(item?.status || "").toLowerCase() === "pending"
    );
    if (!pendingItems.length) {
      setMessage({ type: "error", text: "No pending transfers found in this group." });
      return;
    }
    setSelectedTransfer(group?.parent || pendingItems[0] || null);
    setSelectedTransferGroup(group);
    setDialogMode("accept");
    setOpenTransferConfirm(true);
  };

  const rejectTransferGroup = (group) => {
    const pendingItems = (group?.items || []).filter(
      (item) => String(item?.status || "").toLowerCase() === "pending"
    );
    if (!pendingItems.length) {
      setMessage({ type: "error", text: "No pending transfers found in this group." });
      return;
    }
    setSelectedTransfer(group?.parent || pendingItems[0] || null);
    setSelectedTransferGroup(group);
    setDialogMode("reject");
    setOpenTransferConfirm(true);
  };

  const deleteTransferGroup = async (group) => {
    const transferIds = (group?.items || []).map((item) => item?.id).filter(Boolean);
    if (!transferIds.length) return;

    const confirmMessage = "Delete this transfer? Accepted transfers will be reversed.";
    if (!window.confirm(confirmMessage)) return;

    setDeletingTransferGroupKey(group.key);
    try {
      const headers = auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` };
      for (const id of transferIds) {
        await axios.delete(`${apiBase}/shop/stock-transfers/${id}/`, {
          headers,
        });
      }
      setMessage({ type: "success", text: "Stock transfer deleted successfully." });
      setOpenDetails(false);
      setOpenTransferConfirm(false);
      setSelectedTransfer(null);
      setSelectedTransferGroup(null);
      setDialogMode("view");
      refetch?.();
      refetchAccountTransactions?.();
    } catch (error) {
      setMessage({
        type: "error",
        text: error.response?.data?.detail || "Failed to delete stock transfer.",
      });
    } finally {
      setDeletingTransferGroupKey("");
    }
  };

  const openActionMenu = (event, group) => {
    setActionMenuAnchorEl(event.currentTarget);
    setActionMenuGroup(group);
  };

  const closeActionMenu = () => {
    setActionMenuAnchorEl(null);
    setActionMenuGroup(null);
  };

  const openPaymentDialog = (row) => {
    setSelectedTransfer(row?.parent || row);
    setPaymentData(row);
    setPaymentAccountId("");
    setPaymentDate(new Date().toISOString().slice(0, 10));
    setPaymentDescription(
      `Stock transfer payment for ${row?.parent?.product_details?.name || row?.parent?.product_name || row?.product_details?.name || "product"} - ${
        row?.transferIds?.join(", ") || row?.parent?.transfer_id || row?.transfer_id || ""
      }`.trim()
    );
    setOpenPayment(true);
    refetchAccountTransactions?.();
  };

  const getRowSubtotalAmount = (row) => {
    const unitAmount = Number(
      row?.total_amount ??
        row?.subtotal_amount ??
        row?.retail_price ??
        row?.sale_price ??
        row?.purchase_item_details?.retail_price ??
        row?.purchase_item_details?.sale_price ??
        row?.source_stock_details?.sale_price ??
        row?.source_stock?.sale_price ??
        row?.source_stock_details?.buy_price ??
        row?.source_stock?.buy_price ??
        row?.purchase_item_details?.buy_price ??
        row?.buy_price ??
        0
    );
    const qty = Number(row?.transfer_quantity) || 0;
    const subtotal = row?.total_amount != null ? unitAmount : unitAmount * qty;
    return Number.isFinite(subtotal) ? subtotal : 0;
  };

  const getRowDiscountAmount = (row) => {
    const discount = Number(row?.discount_amount ?? row?.transfer_discount_amount ?? 0);
    return Number.isFinite(discount) ? discount : 0;
  };

  const getRowTotalAmount = (row) => {
    const qty = Number(row?.transfer_quantity) || 0;
    const salePrice = getTransferItemSalePrice(row);
    const computed = qty * salePrice;
    if (computed > 0) return computed;
    const explicitTotal = row?.total_amount ?? row?.transfer_total_amount;
    if (explicitTotal != null && explicitTotal !== "") {
      const total = Number(explicitTotal);
      return Number.isFinite(total) ? total : 0;
    }
    return Math.max(getRowSubtotalAmount(row) - getRowDiscountAmount(row), 0);
  };

  const getRowPayableAmount = (row) => {
    if (Array.isArray(row?.items) && row.items.length) {
      return row.items.reduce((sum, item) => {
        if (item?.show_payment_button === false) return sum;
        return sum + getRowTotalAmount(item);
      }, 0);
    }

    if (row?.show_payment_button === false) return 0;
    return getRowTotalAmount(row);
  };

  const getRowPaidAmount = (row) => {
    const transferIds = row?.transferIds?.length
      ? row.transferIds
      : row?.transfer_id
      ? [row.transfer_id]
      : [];
    if (!transferIds.length) return 0;
    const overrideKey = transferIds.join("|");
    const overrideAmount = Number(paymentOverrides[overrideKey]) || 0;
    const normalizedIds = transferIds.map((id) => String(id).trim()).filter(Boolean);
    return accountTransactions.reduce((sum, txn) => {
      const desc = String(txn?.description || "");
      const transferMetaMatch = desc.match(/\[Transfers:\s*([^\]]+)\]/);
      const describedIds = transferMetaMatch
        ? transferMetaMatch[1].split(",").map((id) => id.trim()).filter(Boolean)
        : [];
      const matchesTransfers = describedIds.length
        ? describedIds.length === normalizedIds.length &&
          describedIds.every((id) => normalizedIds.includes(id))
        : normalizedIds.length === 1 && desc.includes(`- ${normalizedIds[0]}`);
      if (
        txn?.transaction_type === "credit" &&
        matchesTransfers &&
        Number(txn?.amount) > 0
      ) {
        return sum + Number(txn.amount);
      }
      return sum;
    }, overrideAmount);
  };

  const getRowRemainedAmount = (row) => {
    const total = getRowPayableAmount(row);
    const paid = getRowPaidAmount(row);
    return Math.max(total - paid, 0);
  };

  const paymentTotalAmount = useMemo(() => {
    if (!paymentData) return 0;
    return Number(getRowPayableAmount(paymentData)) || 0;
  }, [paymentData]);

  const paymentPaidAmount = useMemo(() => {
    if (!paymentData) return 0;
    const transferIds = paymentData?.transferIds?.length
      ? paymentData.transferIds
      : paymentData?.transfer_id
      ? [paymentData.transfer_id]
      : [];
    if (!transferIds.length) return 0;
    const normalizedIds = transferIds.map((id) => String(id).trim()).filter(Boolean);
    return accountTransactions.reduce((sum, txn) => {
      const desc = String(txn?.description || "");
      const transferMetaMatch = desc.match(/\[Transfers:\s*([^\]]+)\]/);
      const describedIds = transferMetaMatch
        ? transferMetaMatch[1].split(",").map((id) => id.trim()).filter(Boolean)
        : [];
      const matchesTransfers = describedIds.length
        ? describedIds.length === normalizedIds.length &&
          describedIds.every((id) => normalizedIds.includes(id))
        : normalizedIds.length === 1 && desc.includes(`- ${normalizedIds[0]}`);
      if (
        txn?.transaction_type === "credit" &&
        matchesTransfers &&
        Number(txn?.amount) > 0
      ) {
        return sum + Number(txn.amount);
      }
      return sum;
    }, 0);
  }, [accountTransactions, paymentData]);

  const paymentRemainingAmount = useMemo(() => {
    const paid = Number(paymentAmount) || 0;
    const remaining = paymentTotalAmount - paid;
    return Number.isFinite(remaining) && remaining > 0 ? remaining : 0;
  }, [paymentAmount, paymentTotalAmount]);

  const paymentOutstandingAmount = useMemo(() => {
    const outstanding = paymentTotalAmount - paymentPaidAmount;
    return Number.isFinite(outstanding) && outstanding > 0 ? outstanding : 0;
  }, [paymentTotalAmount, paymentPaidAmount]);

  const transferCurrencyBreakdown = useMemo(() => {
    const map = new Map();
    transferItems.forEach((item) => {
      const currencyId = getItemCurrencyId(item) || getItemCurrencyTitle(item) || "unknown";
      const currencyTitle = getItemCurrencyTitle(item) || "-";
      const qty = Number(item.transfer_quantity) || 0;
      const lineTotal = qty * getTransferItemSalePrice(item);
      const current = map.get(currencyId) || { currencyId, currencyTitle, subtotal: 0 };
      current.subtotal += lineTotal;
      if (currencyTitle && current.currencyTitle !== currencyTitle) {
        current.currencyTitle = currencyTitle;
      }
      map.set(currencyId, current);
    });
    return Array.from(map.values()).filter((row) => row.subtotal > 0);
  }, [transferItems, currencies, stockRows, getItemCurrencyId, getItemCurrencyTitle, getTransferItemSalePrice]);

  const transferSubtotalAmount = useMemo(() => {
    return transferCurrencyBreakdown.reduce((sum, row) => sum + row.subtotal, 0);
  }, [transferCurrencyBreakdown]);
  const hasMixedTransferCurrencies = transferCurrencyBreakdown.length > 1;
  const transferCurrencyTitle = useMemo(() => {
    const titles = [...new Set(transferItems.map((item) => getItemCurrencyTitle(item)).filter(Boolean))];
    if (!titles.length) return "";
    if (titles.length === 1) return titles[0];
    return t("stock.mixedCurrencies", "Mixed currencies");
  }, [transferItems, currencies, stockRows, t, getItemCurrencyTitle]);
  const transferDiscountValue = Number(transferDiscountAmount) || 0;
  const transferTotalAmount = Math.max(transferSubtotalAmount - transferDiscountValue, 0);
  const transferParsedReceivedAmount = Number(transferReceivedAmount) || 0;
  const transferRemainedAmount = Math.max(transferTotalAmount - transferParsedReceivedAmount, 0);
  const selectedCustomerBranchAmountOwed = transferTotalAmount;
  const selectedCustomerBranchDetails = useMemo(() => {
    if (!selectedCustomerBranch) return [];
    return [
      { label: t("stock.branchId", "ID"), value: selectedCustomerBranch.name || "--" },
      { label: t("stock.branchName", "Name"), value: selectedCustomerBranch.name || "--" },
      { label: t("stock.branchEmail", "Email"), value: "null" },
      { label: t("stock.branchContact", "Contact"), value: "null" },
      { label: t("stock.branchAddress", "Address"), value: selectedCustomerBranch.name || "--" },
      {
        label: t("customer.openingBalance", "Opening balance (existing dues)"),
        value: "0",
      },
      {
        label: t("customer.amountOwed", "Amount owed"),
        value: selectedCustomerBranchAmountOwed.toFixed(2),
        bold: true,
      },
    ];
  }, [selectedCustomerBranch, selectedCustomerBranchAmountOwed, t]);
  const selectedPaymentAccount = useMemo(
    () => accounts.find((account) => String(account.id) === String(transferPaymentAccountId)),
    [accounts, transferPaymentAccountId]
  );
  const { data: transferDirectRateData } = useFetchObjects(
    [
      "stock-transfer-payment-rate-direct",
      selectedPaymentAccount?.shop,
      selectedPaymentAccount?.currency,
      transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product),
    ],
    selectedPaymentAccount && (transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product))
      ? `shop/exchange-rates/?page_size=1&shop=${selectedPaymentAccount.shop}&from_currency=${selectedPaymentAccount.currency}&to_currency=${transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product)}`
      : null,
    token
  );
  const { data: transferReverseRateData } = useFetchObjects(
    [
      "stock-transfer-payment-rate-reverse",
      selectedPaymentAccount?.shop,
      selectedPaymentAccount?.currency,
      transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product),
    ],
    selectedPaymentAccount && (transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product))
      ? `shop/exchange-rates/?page_size=1&shop=${selectedPaymentAccount.shop}&from_currency=${transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product)}&to_currency=${selectedPaymentAccount.currency}`
      : null,
    token
  );
  const transferExchangeRatePreview = useMemo(() => {
    const firstItemCurrencyId = transferItems[0]?.currency || getDefaultCurrencyIdForProduct(transferItems[0]?.product);
    if (!selectedPaymentAccount || !firstItemCurrencyId) return "";
    if (String(selectedPaymentAccount.currency) === String(firstItemCurrencyId)) return "1";
    const direct = transferDirectRateData?.results?.[0]?.rate;
    if (direct) return String(direct);
    const reverse = transferReverseRateData?.results?.[0]?.rate;
    if (reverse && Number(reverse) > 0) return String(1 / Number(reverse));
    return "";
  }, [
    selectedPaymentAccount,
    transferItems,
    transferDirectRateData,
    transferReverseRateData,
    getDefaultCurrencyIdForProduct,
  ]);

  const getRowRemainingFromPayments = (row) => {
    const total = getRowTotalAmount(row);
    const paid = getRowPaidAmount(row);
    return Math.max(total - paid, 0);
  };

  const getTransferCurrencyTitle = (transfer) => {
    return getItemCurrencyTitle(transfer) || "-";
  };

  const buildCurrencyBreakdown = (items) => {
    const map = new Map();
    (items || []).forEach((item) => {
      const currencyTitle = getTransferCurrencyTitle(item) || "-";
      const qty = Number(item?.transfer_quantity) || 0;
      const lineTotal = qty * getTransferItemSalePrice(item);
      if (!Number.isFinite(lineTotal) || lineTotal <= 0) return;
      map.set(currencyTitle, (map.get(currencyTitle) || 0) + lineTotal);
    });
    return Array.from(map.entries()).map(([currencyTitle, amount]) => ({
      currencyTitle,
      amount,
    }));
  };

  const getRowPaidBreakdown = (row) => {
    const transferIds = row?.transferIds?.length
      ? row.transferIds
      : row?.transfer_id
      ? [row.transfer_id]
      : [];
    if (!transferIds.length) return [];

    const normalizedIds = transferIds.map((id) => String(id).trim()).filter(Boolean);
    const map = new Map();

    accountTransactions.forEach((txn) => {
      const desc = String(txn?.description || "");
      const transferMetaMatch = desc.match(/\[Transfers:\s*([^\]]+)\]/);
      const describedIds = transferMetaMatch
        ? transferMetaMatch[1].split(",").map((id) => id.trim()).filter(Boolean)
        : [];
      const matchesTransfers = describedIds.length
        ? describedIds.length === normalizedIds.length &&
          describedIds.every((id) => normalizedIds.includes(id))
        : normalizedIds.length === 1 && desc.includes(`- ${normalizedIds[0]}`);

      if (
        txn?.transaction_type !== "credit" ||
        !matchesTransfers ||
        !(Number(txn?.amount) > 0)
      ) {
        return;
      }

      const currencyTitle =
        txn?.transaction_currency_title ||
        getCurrencyTitleById(txn?.transaction_currency) ||
        "-";
      map.set(currencyTitle, (map.get(currencyTitle) || 0) + Number(txn.amount));
    });

    return Array.from(map.entries()).map(([currencyTitle, amount]) => ({
      currencyTitle,
      amount,
    }));
  };

  const getRemainingBreakdown = (items, row) => {
    const totalMap = new Map();
    buildCurrencyBreakdown(items).forEach((entry) => {
      totalMap.set(entry.currencyTitle, entry.amount);
    });

    getRowPaidBreakdown(row).forEach((entry) => {
      const current = totalMap.get(entry.currencyTitle) || 0;
      totalMap.set(entry.currencyTitle, Math.max(current - entry.amount, 0));
    });

    return Array.from(totalMap.entries())
      .filter(([, amount]) => amount > 0)
      .map(([currencyTitle, amount]) => ({ currencyTitle, amount }));
  };

  const groupedTransfers = useMemo(() => {
    const groups = [];
    const explicitGroups = new Map();
    const legacyGroups = new Map();

    safeTransfers.forEach((row) => {
      const explicitGroupId = String(row?.transfer_group_id || "");
      const rowTime = row?.created_at ? new Date(row.created_at).getTime() : 0;

      if (explicitGroupId) {
        const mapKey = `group:${explicitGroupId}`;
        const existingGroup = explicitGroups.get(mapKey);
        if (existingGroup) {
          existingGroup.items.push(row);
          existingGroup.lastRowTime = Math.max(existingGroup.lastRowTime || 0, rowTime || 0);
          return;
        }

        const group = {
          key: mapKey,
          baseKey: mapKey,
          parent: row,
          items: [row],
          lastRowTime: rowTime || 0,
        };
        explicitGroups.set(mapKey, group);
        groups.push(group);
        return;
      }

      const legacyKey = [
        "legacy",
        String(row?.supplier_branch ?? ""),
        String(row?.customer_branch ?? ""),
        String(row?.transfer_date ?? ""),
        String(row?.created_by ?? ""),
        String(row?.status ?? ""),
        String(row?.notes ?? ""),
      ].join("|");
      const existingGroup = legacyGroups.get(legacyKey);

      if (existingGroup) {
        existingGroup.items.push(row);
        existingGroup.lastRowTime = Math.max(existingGroup.lastRowTime || 0, rowTime || 0);
        return;
      }

      const group = {
        key: legacyKey,
        baseKey: legacyKey,
        parent: row,
        items: [row],
        lastRowTime: rowTime || 0,
      };
      legacyGroups.set(legacyKey, group);
      groups.push(group);
    });

    return groups.map((group) => {
      const subtotal = group.items.reduce((sum, item) => {
        const qty = Number(item?.transfer_quantity) || 0;
        const price = getTransferItemSalePrice(item);
        return sum + qty * price;
      }, 0);
      const currencyBreakdown = buildCurrencyBreakdown(group.items);
      
      const discountAmount = getRowDiscountAmount(group.parent);
      const totalAmount = Math.max(subtotal - discountAmount, 0);
      
      const transferIds = group.items.map((item) => item?.transfer_id).filter(Boolean);
      const remainedAmount = getRowRemainedAmount({ ...group.parent, items: group.items, transferIds });
      const remainedBreakdown = getRemainingBreakdown(group.items, {
        ...group.parent,
        items: group.items,
        transferIds,
      });
      const totalQuantity = group.items.reduce((sum, item) => sum + (Number(item?.transfer_quantity) || 0), 0);
      
      // Determine display name
      const productNames = group.items.map(item => item.product_details?.name).filter(Boolean);
      const uniqueProductNames = [...new Set(productNames)];
      const displayProductName = uniqueProductNames.length === 1 
        ? uniqueProductNames[0] 
        : uniqueProductNames.length > 1 
          ? t("stock.multipleProducts", "Multiple Products")
          : "-";

      return { 
        ...group, 
        displayProductName,
        subtotal,
        currencyBreakdown,
        totalAmount, 
        discountAmount, 
        remainedBreakdown,
        remainedAmount, 
        totalQuantity, 
        transferIds 
      };
    });
  }, [safeTransfers, accountTransactions, paymentOverrides, t]);

  const pagedTransfers = useMemo(() => {
    if (rowsPerPage === -1) return groupedTransfers;
    const start = page * rowsPerPage;
    return groupedTransfers.slice(start, start + rowsPerPage);
  }, [groupedTransfers, page, rowsPerPage]);

  useEffect(() => {
    if (!openPayment || !paymentData) return;
    setPaymentAmount(
      String((paymentOutstandingAmount > 0 ? paymentOutstandingAmount : paymentTotalAmount) || 0).replace(/\.00$/, "")
    );
  }, [openPayment, paymentData, paymentOutstandingAmount]);

  useEffect(() => {
    if (!showPaymentBranch) return;
    if (!transferPaymentAccountId) {
      setTransferExchangeRate("");
      return;
    }
    setTransferExchangeRate(transferExchangeRatePreview || "");
  }, [showPaymentBranch, transferPaymentAccountId, transferExchangeRatePreview]);

  useEffect(() => {
    if (!openForm) return;
    setTransferPaymentAccountId("");
    setTransferExchangeRate("");
    setTransferDiscountAmount("");
  }, [openForm]);

  const escapeHtml = (value) =>
    String(value ?? "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");

  const openA4PrintWindow = ({
    title,
    subtitle,
    headerMeta = [],
    cards = [],
    tableHtml = "",
    note = "",
  }) => {
    const printWindow = window.open("", "_blank", "width=1200,height=900");
    if (!printWindow) return null;

    const metaHtml = headerMeta
      .map(
        (item) => `
          <div class="meta-item">
            <div class="meta-label">${escapeHtml(item.label)}</div>
            <div class="meta-value">${escapeHtml(item.value)}</div>
          </div>
        `
      )
      .join("");

    const cardsHtml = cards
      .map(
        (item) => `
          <div class="card">
            <div class="card-label">${escapeHtml(item.label)}</div>
            <div class="card-value">${escapeHtml(item.value)}</div>
          </div>
        `
      )
      .join("");

    printWindow.document.open();
    printWindow.document.write(`
      <!doctype html>
      <html>
        <head>
          <title>${escapeHtml(title)}</title>
          <style>
            @page {
              size: A4;
              margin: 12mm;
            }
            html, body {
              width: 100%;
              margin: 0;
              padding: 0;
              background: #fff;
              color: #111827;
              font-family: Arial, Helvetica, sans-serif;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            * { box-sizing: border-box; }
            .page { width: 100%; min-height: 100%; }
            .header {
              display: flex;
              justify-content: space-between;
              gap: 16px;
              align-items: flex-start;
              padding-bottom: 14px;
              border-bottom: 2px solid #1e3a8a;
              margin-bottom: 14px;
            }
            .brand { display: grid; gap: 4px; }
            .brand .title {
              font-size: 24px;
              font-weight: 800;
              letter-spacing: 0.4px;
              color: #0f172a;
            }
            .brand .subtitle {
              font-size: 12px;
              color: #475569;
            }
            .header-meta {
              display: grid;
              gap: 8px;
              min-width: 220px;
              text-align: right;
            }
            .meta-item { display: grid; gap: 2px; }
            .meta-label {
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.08em;
              color: #64748b;
            }
            .meta-value {
              font-size: 12px;
              font-weight: 700;
              color: #0f172a;
              word-break: break-word;
            }
            .summary-grid {
              display: grid;
              grid-template-columns: repeat(4, minmax(0, 1fr));
              gap: 10px;
              margin: 0 0 14px;
            }
            .card {
              border: 1px solid #dbe4f0;
              border-radius: 10px;
              padding: 10px 12px;
              background: linear-gradient(180deg, #f8fbff 0%, #ffffff 100%);
            }
            .card-label {
              font-size: 10px;
              letter-spacing: 0.08em;
              text-transform: uppercase;
              color: #64748b;
              margin-bottom: 4px;
            }
            .card-value {
              font-size: 14px;
              font-weight: 800;
              color: #0f172a;
              word-break: break-word;
            }
            .section-title {
              font-size: 14px;
              font-weight: 800;
              color: #0f172a;
              margin: 14px 0 8px;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              table-layout: fixed;
            }
            thead { display: table-header-group; }
            tr { page-break-inside: avoid; }
            th, td {
              border: 1px solid #d8e1ec;
              padding: 8px 9px;
              font-size: 11px;
              vertical-align: top;
              word-break: break-word;
            }
            th {
              background: #102a43;
              color: #fff;
              font-weight: 700;
              text-transform: uppercase;
              letter-spacing: 0.04em;
              font-size: 10px;
            }
            tbody tr:nth-child(even) td { background: #f8fbff; }
            .note {
              margin-top: 12px;
              font-size: 10px;
              color: #64748b;
              line-height: 1.5;
            }
            .footer {
              margin-top: 14px;
              padding-top: 8px;
              border-top: 1px solid #dbe4f0;
              font-size: 10px;
              color: #64748b;
              display: flex;
              justify-content: space-between;
              gap: 12px;
            }
            @media (max-width: 768px) {
              .header, .footer { flex-direction: column; text-align: left; }
              .header-meta { text-align: left; }
              .summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
            }
          </style>
        </head>
        <body>
          <div class="page">
            <div class="header">
              <div class="brand">
                <div class="title">${escapeHtml(title)}</div>
                <div class="subtitle">${escapeHtml(subtitle)}</div>
              </div>
              <div class="header-meta">${metaHtml}</div>
            </div>
            ${cardsHtml ? `<div class="summary-grid">${cardsHtml}</div>` : ""}
            ${tableHtml ? `<div class="section-title">Details</div>${tableHtml}` : ""}
            ${note ? `<div class="note">${escapeHtml(note)}</div>` : ""}
            <div class="footer">
              <div>Taj Zoi Inventory</div>
              <div>Printed at ${escapeHtml(new Date().toLocaleString())}</div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    return printWindow;
  };

  const printTransferDetails = (transferOrGroup) => {
    if (!transferOrGroup) return;
    const isGroup = Array.isArray(transferOrGroup?.items);
    const transfer = isGroup ? transferOrGroup.parent || transferOrGroup.items[0] : transferOrGroup;
    const items = isGroup ? transferOrGroup.items || [] : [transfer];
    const totalQuantity = isGroup
      ? transferOrGroup.totalQuantity
      : Number(transfer?.transfer_quantity || 0);
    const totalAmount = isGroup
      ? transferOrGroup.totalAmount
      : (Number(transfer?.transfer_quantity || 0) * getTransferItemSalePrice(transfer));
    const status = isGroup
      ? transferOrGroup.items.every((item) => item?.status === "accepted")
        ? "Accepted"
        : transferOrGroup.items.every((item) => item?.status === "rejected")
        ? "Rejected"
        : transferOrGroup.items.every((item) => item?.status === "pending")
        ? "Pending"
        : "Partial"
      : transfer.status_display || transfer.status || "-";

    const printable = openA4PrintWindow({
      title: "Stock Transfer Receipt",
      subtitle: isGroup ? "Transfer group print" : "Individual transfer print",
      headerMeta: [
        { label: "Transfer ID", value: isGroup ? transferOrGroup.key : transfer.transfer_id || "-" },
        { label: "Created By", value: transfer.transfer_user_details?.username || "-" },
        { label: "Transfer Date", value: transfer.transfer_date || "-" },
        { label: "Status", value: status },
      ],
      cards: [
        { label: "Product", value: isGroup ? transferOrGroup.displayProductName : transfer.product_details?.name || "-" },
        { label: "From Province", value: transfer.supplier_branch_details?.name || branchNameById.get(String(transfer.supplier_branch)) || "-" },
        { label: "To Province", value: transfer.customer_branch_details?.name || branchNameById.get(String(transfer.customer_branch)) || "-" },
        { label: "Quantity", value: String(totalQuantity || "-") },
        { label: "Total Amount", value: Number(totalAmount || 0).toFixed(2) },
      ],
      tableHtml: `
        <table>
          <thead>
            <tr>
              <th style="width: 18%">Product</th>
              <th style="width: 8%">Qty</th>
              <th style="width: 11%">Buy Price</th>
              <th style="width: 11%">Wholesale</th>
              <th style="width: 11%">Retail</th>
              <th style="width: 10%">Currency</th>
              <th style="width: 11%">Subtotal</th>
              <th style="width: 20%">Notes</th>
            </tr>
          </thead>
          <tbody>
            ${items.map((item) => {
              const qty = Number(item?.transfer_quantity || 0);
              const buyPrice = getTransferItemSalePrice(item);
              return `
                <tr>
                  <td>${escapeHtml(item?.product_details?.name || "-")}</td>
                  <td>${escapeHtml(item?.transfer_quantity ?? "-")}</td>
                  <td>${escapeHtml(buyPrice.toFixed(2))}</td>
                  <td>${escapeHtml(getTransferItemSourcePrices(item).wholesale_price || "-")}</td>
                  <td>${escapeHtml(getTransferItemSourcePrices(item).retail_price || "-")}</td>
                  <td>${escapeHtml(getTransferCurrencyTitle(item) || "-")}</td>
                  <td>${escapeHtml((qty * buyPrice).toFixed(2))}</td>
                  <td>${escapeHtml(item?.notes || "-")}</td>
                </tr>
              `;
            }).join("")}
          </tbody>
        </table>
      `,
      note: "This A4 layout is optimized for a professional stock transfer printout.",
    });
    if (!printable) return;
    printable.onload = () => {
      printable.print();
      printable.close();
    };
  };

  const openAcceptFromStock = (row) => {
    setSelectedTransfer({
      ...row,
      product_details: row?.purchase_item_details?.product,
      source_stock_details: {
        id: row?.id,
        product: row?.purchase_item_details?.product,
        buy_price: row?.purchase_item_details?.buy_price,
        wholesale_price: row?.purchase_item_details?.wholesale_price,
        retail_price: row?.purchase_item_details?.retail_price || row?.purchase_item_details?.sale_price,
        quantity: row?.quantity,
        unit: row?.unit_details,
      },
      supplier_branch_details: row?.shop_details,
    });
    setPendingTransfer({
      product: row?.purchase_item_details?.product?.id || "",
      supplier_branch: row?.shop_details?.id || "",
      customer_branch: "",
      transfer_quantity: "",
      transfer_date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setSelectedTransferGroup(null);
    setDialogMode("accept");
    setOpenTransferConfirm(true);
  };

  const openNewTransferDialog = () => {
    setDialogMode("create");
    setSelectedTransfer(null);
    setSelectedTransferGroup(null);
    setPendingTransfer(null);
    setForm({
      product: "",
      supplier_branch: resolvedSelectedBranchId,
      customer_branch: "",
      transfer_quantity: "",
      transfer_date: new Date().toISOString().slice(0, 10),
      notes: "",
    });
    setTransferItems([createEmptyTransferItem()]);
    setShowPaymentBranch(false);
    setTransferPaymentAccountId("");
    setTransferExchangeRate("");
    setTransferDiscountAmount("");
    setTransferReceivedAmount("");
    setOpenForm(true);
  };

  const openEditTransferDialog = (group) => {
    const editItems = (group?.items || []).map((item) => ({
        id: item.id,
        product: String(item?.product_details?.id || item?.product || ""),
        currency: String(item?.currency_details?.id || item?.currency || item?.source_stock_details?.currency?.id || item?.source_stock_details?.currency || ""),
        product_details: item?.product_details || item?.source_stock_details?.product || item?.purchase_item_details?.product || null,
        source_stock_details: item?.source_stock_details || item?.purchase_item_details || null,
        transfer_quantity: String(item?.transfer_quantity ?? ""),
        ...getTransferItemSourcePrices(item),
        buy_price: String(getTransferItemSourcePrices(item).buy_price ?? ""),
        buy_price_touched: false,
        wholesale_price: String(getTransferItemSourcePrices(item).wholesale_price ?? ""),
        wholesale_price_touched: false,
        retail_price: String(getTransferItemSourcePrices(item).retail_price ?? ""),
        retail_price_touched: false,
        sale_price: String(getTransferItemSourcePrices(item).sale_price ?? ""),
        sale_price_touched: false,
      }));

    if (!editItems.length) {
      setMessage({ type: "error", text: "No transfers found to edit." });
      return;
    }

    const parent = group?.parent || group?.items?.[0] || {};
    setDialogMode("edit");
    setSelectedTransfer(parent);
    setSelectedTransferGroup(group);
    setPendingTransfer(null);
    setForm({
      product: editItems[0]?.product || "",
      supplier_branch: String(parent?.supplier_branch || ""),
      customer_branch: String(parent?.customer_branch || ""),
      transfer_quantity: editItems[0]?.transfer_quantity || "",
      transfer_date: parent?.transfer_date || new Date().toISOString().slice(0, 10),
      notes: parent?.notes || "",
    });
    setTransferItems(editItems);
    setShowPaymentBranch(Boolean(parent?.show_payment_button));
    setTransferPaymentAccountId("");
    setTransferExchangeRate("");
    setTransferDiscountAmount(String(parent?.discount_amount || 0));
    setTransferReceivedAmount("");
    setOpenForm(true);
  };

  useEffect(() => {
    if (!openForm) {
      setForm({
        product: "",
        supplier_branch: resolvedSelectedBranchId,
        customer_branch: "",
        transfer_quantity: "",
        transfer_date: new Date().toISOString().slice(0, 10),
        notes: "",
      });
      setTransferItems([createEmptyTransferItem()]);
      setShowPaymentBranch(false);
      setTransferPaymentAccountId("");
      setTransferExchangeRate("");
      setTransferDiscountAmount("");
      setTransferReceivedAmount("");
    }
  }, [openForm, resolvedSelectedBranchId]);

  useEffect(() => {
    if (!resolvedSelectedBranchId) return;
    setForm((prev) => ({
      ...prev,
      supplier_branch: resolvedSelectedBranchId,
    }));
  }, [resolvedSelectedBranchId]);

  const submitTransfer = async () => {
    return submitTransferAction(false);
  };

  const submitTransferAction = async (acceptAfterCreate = false) => {
    try {
      const transferData = pendingTransfer || form;
      const pendingGroupItems = (selectedTransferGroup?.items || []).filter(
        (item) => String(item?.status || "").toLowerCase() === "pending"
      );
      const isGroupAcceptAction = dialogMode === "accept" && pendingGroupItems.length > 0;
      const isGroupRejectAction = dialogMode === "reject" && pendingGroupItems.length > 0;
      const isAcceptAction = dialogMode === "accept" && Boolean(selectedTransfer?.id && selectedTransfer?.status === "pending");
      const isRejectAction = dialogMode === "reject" && Boolean(selectedTransfer?.id && selectedTransfer?.status === "pending");
      const validItems = transferItems.filter(
        (item) => item.product && item.transfer_quantity && Number(item.transfer_quantity) > 0
      );
      const selectedPaymentAccount = accounts.find(
        (account) => String(account.id) === String(transferPaymentAccountId)
      );
      const shouldRecordPayment = false;

      if (!isAcceptAction && !isRejectAction) {
        if (!validItems.length || !transferData.supplier_branch || !transferData.customer_branch) {
          setMessage({
            type: "error",
            text: "Please fill all required fields: Product, From Province, To Province, and Send Quantity.",
          });
          return;
        }

        if (String(transferData.supplier_branch) === String(transferData.customer_branch)) {
          setMessage({ type: "error", text: "Source and destination provinces must be different." });
          return;
        }
      }

      if (dialogMode === "edit") {
        const editableItems = selectedTransferGroup?.items || [];
        if (!editableItems.length) {
          setMessage({ type: "error", text: "No transfers found to edit." });
          return;
        }

        const headers = auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` };
        const editGroupId = String(
          selectedTransferGroup?.parent?.transfer_group_id ||
            `TRG-${transferData.supplier_branch}-${transferData.customer_branch}-${Date.now()}`
        ).slice(0, 64);
        const validEditItems = transferItems.filter(
          (item) => item.product && item.transfer_quantity && Number(item.transfer_quantity) > 0
        );
        const originalIds = editableItems.map((item) => String(item?.id)).filter(Boolean);
        const keptIds = validEditItems.map((item) => String(item?.id || "")).filter(Boolean);

        for (let index = 0; index < validEditItems.length; index += 1) {
          const draft = validEditItems[index];
          const originalIndex = editableItems.findIndex((item) => String(item?.id) === String(draft?.id));
          const item = originalIndex >= 0 ? editableItems[originalIndex] : null;
          const validationIndex = originalIndex >= 0 ? originalIndex : index;
          const requestedQuantity = Number(draft.transfer_quantity || item?.transfer_quantity || 0);
          const availableQuantity = Number(
            getAvailableQuantityForItem(
              { ...item, ...draft, product: draft.product || item?.product_details?.id || item?.product || "" },
              validationIndex,
              Boolean(item?.id)
            )
          ) || 0;
          if (requestedQuantity > availableQuantity) {
            setMessage({
              type: "error",
              text: `Send quantity cannot be greater than available quantity (${availableQuantity}).`,
            });
            return;
          }

          const productId = draft.product || item?.product_details?.id || item?.product || "";
          const buyPrice = getTransferItemPriceValue(draft, "buy_price");
          const payload = {
            product: Number(productId),
            transfer_group_id: editGroupId,
            supplier_branch: Number(transferData.supplier_branch),
            customer_branch: Number(transferData.customer_branch),
            currency: Number(draft.currency || item?.currency || getDefaultCurrencyIdForProduct(productId)) || "",
            transfer_quantity: requestedQuantity,
            buy_price: buyPrice,
            wholesale_price: getTransferItemPriceValue(draft, "wholesale_price"),
            retail_price: getTransferItemPriceValue(draft, "retail_price"),
            sale_price: buyPrice || getTransferItemPriceValue(draft, "retail_price") || getTransferItemPriceValue(draft, "sale_price") || getTransferItemPriceValue(draft, "wholesale_price") || "",
            discount_amount: index === 0 ? Number(transferDiscountAmount || 0) : 0,
            transfer_date: transferData.transfer_date || new Date().toISOString().slice(0, 10),
            show_payment_button: showPaymentBranch,
            notes: transferData.notes || "",
          };

          if (draft.id) {
            await axios.patch(`${apiBase}/shop/stock-transfers/${draft.id}/`, payload, { headers });
          } else {
            await axios.post(`${apiBase}/shop/stock-transfers/`, payload, { headers });
          }
        }

        const removedIds = originalIds.filter((id) => !keptIds.includes(id));
        for (const id of removedIds) {
          await axios.delete(`${apiBase}/shop/stock-transfers/${id}/`, { headers });
        }

        setMessage({ type: "success", text: "Stock transfer updated successfully." });
      } else if (isGroupAcceptAction) {
        for (const item of pendingGroupItems) {
          const sourcePrices = item?.source_stock_details || item?.purchase_item_details || item || {};
          const payload = {
            product: Number(item?.product?.id || item?.product_details?.id || 0),
            supplier_branch: Number(item?.supplier_branch),
            customer_branch: Number(item?.customer_branch),
            currency: Number(item?.currency || getDefaultCurrencyIdForProduct(item?.product?.id || item?.product_details?.id || item?.product || "")) || "",
            transfer_quantity: Number(item?.transfer_quantity),
            buy_price: sourcePrices?.buy_price ?? item?.buy_price ?? "",
            wholesale_price: sourcePrices?.wholesale_price ?? item?.wholesale_price ?? "",
            retail_price:
              sourcePrices?.retail_price ??
              sourcePrices?.sale_price ??
              item?.retail_price ??
              "",
            sale_price:
              sourcePrices?.buy_price ||
              item?.retail_price ||
              item?.sale_price ||
              sourcePrices?.retail_price ||
              sourcePrices?.sale_price ||
              "",
            transfer_date: item?.transfer_date || new Date().toISOString().slice(0, 10),
            show_payment_button: Boolean(item?.show_payment_button),
            notes: item?.notes || "",
          };
          await axios.post(`${apiBase}/shop/stock-transfers/${item.id}/accept/`, payload, {
            headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          });
        }
        setMessage({ type: "success", text: "Transfer group accepted successfully." });
      } else if (isGroupRejectAction) {
        for (const item of pendingGroupItems) {
          await axios.post(`${apiBase}/shop/stock-transfers/${item.id}/reject/`, {}, {
            headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          });
        }
        setMessage({ type: "success", text: "Transfer group rejected successfully." });
      } else if (isAcceptAction) {
        const sourcePrices =
          transferData?.source_stock_details || transferData?.purchase_item_details || transferData || {};
        const payload = {
          product: Number(
            transferData?.product?.id ||
              transferData?.product ||
              transferData?.product_details?.id ||
              0
          ),
          supplier_branch: Number(
            transferData?.supplier_branch?.id ||
              transferData?.supplier_branch ||
              transferData?.supplier_branch_details?.id ||
              0
          ),
          customer_branch: Number(
            transferData?.customer_branch?.id ||
              transferData?.customer_branch ||
              transferData?.customer_branch_details?.id ||
              0
          ),
          currency: Number(
            transferData?.currency?.id ||
              transferData?.currency ||
              transferData?.currency_details?.id ||
              getDefaultCurrencyIdForProduct(transferData?.product?.id || transferData?.product || transferData?.product_details?.id || "")
          ) || "",
          transfer_quantity: Number(transferData.transfer_quantity),
          buy_price: sourcePrices?.buy_price ?? transferData.buy_price ?? "",
          wholesale_price: sourcePrices?.wholesale_price ?? transferData.wholesale_price ?? "",
          retail_price:
            sourcePrices?.retail_price ?? sourcePrices?.sale_price ?? transferData.retail_price ?? "",
          sale_price:
            sourcePrices?.buy_price ||
            transferData.retail_price ||
            transferData.sale_price ||
            sourcePrices?.retail_price ||
            sourcePrices?.sale_price ||
            transferData.wholesale_price ||
            "",
          transfer_date: transferData.transfer_date || new Date().toISOString().slice(0, 10),
          show_payment_button: showPaymentBranch,
          notes: transferData.notes || "",
        };
        await axios.post(`${apiBase}/shop/stock-transfers/${selectedTransfer.id}/accept/`, payload, {
          headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
        });
        if (shouldRecordPayment) {
          await axios.post(
            `${apiBase}/shop/account-transactions/`,
            {
              account: transferPaymentAccountId,
              transaction_currency: selectedPaymentAccount.currency,
              transaction_type: "credit",
              amount: String(Number(transferReceivedAmount).toFixed(2)),
              description:
                `Stock transfer payment for ${transferData?.product_details?.name || "product"} - ${
                  selectedTransfer?.transfer_id || ""
                }`.trim(),
              transaction_date: transferData.transfer_date || new Date().toISOString().slice(0, 10),
            },
            {
              headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
            }
          );
        }
        setMessage({ type: "success", text: "Stock transfer accepted successfully." });
      } else if (isRejectAction) {
        await axios.post(`${apiBase}/shop/stock-transfers/${selectedTransfer.id}/reject/`, {}, {
          headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
        });
        setMessage({ type: "success", text: "Stock transfer rejected successfully." });
      } else {
        const newTransferGroupId = `TRG-${transferData.supplier_branch}-${transferData.customer_branch}-${Date.now()}`;
        for (let index = 0; index < validItems.length; index += 1) {
          const item = validItems[index];
          const availableQuantity = Number(getAvailableQuantityForItem(item, index, false)) || 0;
          if (Number(item.transfer_quantity) > availableQuantity) {
            setMessage({
              type: "error",
              text: `Send quantity cannot be greater than available quantity (${availableQuantity}).`,
            });
            return;
          }
          const payload = {
            product: Number(item.product),
            transfer_group_id: newTransferGroupId,
            supplier_branch: Number(transferData.supplier_branch),
            customer_branch: Number(transferData.customer_branch),
            currency: Number(item.currency || getDefaultCurrencyIdForProduct(item.product)) || "",
            transfer_quantity: Number(item.transfer_quantity),
            buy_price: item.buy_price,
            wholesale_price: item.wholesale_price,
            retail_price: item.retail_price,
            sale_price: item.buy_price || item.retail_price || item.sale_price || item.wholesale_price || "",
            discount_amount: Number(transferDiscountAmount || 0),
            transfer_date: transferData.transfer_date || new Date().toISOString().slice(0, 10),
            show_payment_button: showPaymentBranch,
            notes: transferData.notes || "",
          };
          await axios.post(`${apiBase}/shop/stock-transfers/`, payload, {
            headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
          });
        }
        if (shouldRecordPayment) {
          await axios.post(
            `${apiBase}/shop/account-transactions/`,
            {
              account: transferPaymentAccountId,
              transaction_currency: selectedPaymentAccount.currency,
              transaction_type: "credit",
              amount: String(Number(transferReceivedAmount).toFixed(2)),
              description: `Stock transfer payment for ${
                validItems.map((item) => item.product).join(", ") || "transfer"
              }`.trim(),
              transaction_date: transferData.transfer_date || new Date().toISOString().slice(0, 10),
            },
            {
              headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
            }
          );
        }
        setMessage({ type: "success", text: "Stock transfer created successfully." });
        if (acceptAfterCreate) {
          setMessage({ type: "success", text: "Stock transfer created and accepted successfully." });
        }
      }

      setOpenForm(false);
      setOpenTransferConfirm(false);
      setPendingTransfer(null);
      setSelectedTransferGroup(null);
      setSelectedTransfer(null);
      setDialogMode("view");
      refetch();
    } catch (error) {
      const responseData = error?.response?.data;
      const statusCode = error?.response?.status;
      const errorDetail =
        responseData?.detail ||
        responseData?.message ||
        responseData?.error ||
        (typeof responseData === "string" ? responseData.slice(0, 180) : "") ||
        (statusCode ? `Transfer failed with status ${statusCode}.` : "Transfer failed.");
      setMessage({ type: "error", text: errorDetail });
      console.error("Stock transfer error:", error.response?.data || error);
    }
  };

  const printReport = () => {
    if (!groupedTransfers.length) return;

    const printRows = groupedTransfers.flatMap((group) => {
      const row = group.parent || {};
      const fromBranch = row?.supplier_branch_details?.name || branchNameById.get(String(row?.supplier_branch)) || "-";
      const toBranch = row?.customer_branch_details?.name || branchNameById.get(String(row?.customer_branch)) || "-";
      const transferDate = row?.transfer_date || "-";
      const user = row?.transfer_user_details?.username || "-";

      return group.items.map((item) => ({
        product: item?.product_details?.name || "-",
        quantity: item?.transfer_quantity ?? "-",
        currency: getTransferCurrencyTitle(item) || "-",
        salePrice: getTransferItemSalePrice(item).toFixed(2),
        fromBranch,
        toBranch,
        transferDate,
        user,
        subtotal: ((Number(item?.transfer_quantity) || 0) * getTransferItemSalePrice(item)).toFixed(2),
      }));
    });

    const totalQty = printRows.reduce((sum, row) => sum + Number(row.quantity || 0), 0);
    const totalSubtotal = printRows.reduce((sum, row) => sum + Number(row.subtotal || 0), 0);
    const uniqueProducts = new Set(printRows.map((row) => row.product).filter(Boolean)).size;
    const uniqueCurrencies = new Set(printRows.map((row) => row.currency).filter(Boolean));
    const printable = openA4PrintWindow({
      title: "Stock Transfer Report",
      subtitle: "Detailed transfer items by product",
      headerMeta: [
        { label: "Printed On", value: new Date().toLocaleString() },
        { label: "Rows", value: String(printRows.length) },
        { label: "Total Qty", value: String(totalQty) },
        { label: "Total Amount", value: totalSubtotal.toFixed(2) },
      ],
      cards: [
        { label: "Transfers", value: String(groupedTransfers.length) },
        { label: "Products", value: String(uniqueProducts) },
        { label: "Currency", value: uniqueCurrencies.size > 1 ? "Mixed" : (printRows[0]?.currency || "-") },
        { label: "Mode", value: "Buy price" },
      ],
      tableHtml: `
        <table>
          <thead>
            <tr>
              <th style="width: 18%">Product</th>
              <th style="width: 8%">Qty</th>
              <th style="width: 10%">Currency</th>
              <th style="width: 10%">Buy Price</th>
              <th style="width: 10%">Subtotal</th>
              <th style="width: 14%">From Province</th>
              <th style="width: 14%">To Province</th>
              <th style="width: 10%">Date</th>
              <th style="width: 6%">User</th>
            </tr>
          </thead>
          <tbody>
            ${printRows
              .map(
                (row) => `
                  <tr>
                    <td>${escapeHtml(row.product)}</td>
                    <td>${escapeHtml(row.quantity)}</td>
                    <td>${escapeHtml(row.currency)}</td>
                    <td>${escapeHtml(row.salePrice)}</td>
                    <td>${escapeHtml(row.subtotal)}</td>
                    <td>${escapeHtml(row.fromBranch)}</td>
                    <td>${escapeHtml(row.toBranch)}</td>
                    <td>${escapeHtml(row.transferDate)}</td>
                    <td>${escapeHtml(row.user)}</td>
                  </tr>
                `
              )
              .join("")}
          </tbody>
        </table>
      `,
      note: "Buy price is used as the transfer value in this report.",
    });
    if (!printable) return;
    printable.onload = () => {
      printable.print();
      printable.close();
    };
  };

  const submitPayment = async () => {
    setPaymentSubmitting(true);
    try {
      const account = accounts.find((a) => String(a.id) === String(paymentAccountId));
      if (!account) {
        setMessage({ type: "error", text: "Please select an account." });
        return;
      }
      const amount = Number(paymentAmount);
      if (!Number.isFinite(amount) || amount <= 0) {
        setMessage({ type: "error", text: "Payment amount must be greater than 0." });
        return;
      }
      const maxAllowedAmount = paymentOutstandingAmount > 0 ? paymentOutstandingAmount : paymentTotalAmount;
      if (amount > maxAllowedAmount) {
        setMessage({ type: "error", text: "Payment amount cannot be greater than the total amount." });
        return;
      }
      const transferIds = paymentData?.transferIds || (paymentData?.transfer_id ? [paymentData.transfer_id] : []);
      const metadata = `[Transfers: ${transferIds.join(", ")}]`;
      const payload = {
        account: paymentAccountId,
        transaction_currency: account.currency,
        transaction_type: "credit",
        amount: paymentAmount,
        description: `${paymentDescription.trim()}${paymentDescription.trim() ? " " : ""}${metadata}`.trim(),
        transaction_date: paymentDate,
      };
      await axios.post(`${apiBase}/shop/account-transactions/`, payload, {
        headers: auth?.buildAuthHeader?.() || { Authorization: `Token ${token}` },
      });
      setMessage({ type: "success", text: "Payment added to account successfully." });
      await refetchAccountTransactions?.();
      if (paymentData?.transferIds?.length) {
        const overrideKey = paymentData.transferIds.join("|");
        setPaymentOverrides((prev) => ({
          ...prev,
          [overrideKey]: (Number(prev[overrideKey]) || 0) + amount,
        }));
      } else if (paymentData?.transfer_id) {
        const overrideKey = String(paymentData.transfer_id);
        setPaymentOverrides((prev) => ({
          ...prev,
          [overrideKey]: (Number(prev[overrideKey]) || 0) + amount,
        }));
      }
      setOpenPayment(false);
      setPaymentData(null);
      setPaymentAmount("");
      refetch();
      setSelectedTransfer(null);
    } catch (error) {
      const errorDetail =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        "Payment failed.";
      setMessage({ type: "error", text: errorDetail });
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const summary = useMemo(() => {
    const totalTransfers = safeTransfers.length;
    const totalSentQuantity = safeTransfers.reduce((acc, item) => acc + num(item.transfer_quantity), 0);
    return { totalTransfers, totalSentQuantity, totalReceivedQuantity: totalSentQuantity };
  }, [safeTransfers]);

  return (
    <Box id="stock-transfer-print" sx={{ p: 3, pt: 5 }}>
      <GlobalStyles
        styles={{
          "@keyframes toastProgress": {
            "0%": { transform: "scaleX(1)" },
            "100%": { transform: "scaleX(0)" },
          },
          "@media print": {
            body: { visibility: "hidden" },
            "#stock-transfer-print": {
              visibility: "visible",
              position: "absolute",
              left: 0,
              top: 0,
              width: "100%",
              padding: 0,
              margin: 0,
            },
            "#stock-transfer-print *": { visibility: "visible" },
            "#stock-transfer-print .no-print": { display: "none !important" },
            "#stock-transfer-print .MuiDialog-root": { display: "none !important" },
            "#stock-transfer-print .MuiButton-root": { display: "none !important" },
            "#stock-transfer-print .MuiSelect-root": { display: "none !important" },
            "#stock-transfer-print .MuiTextField-root": { display: "none !important" },
            "#stock-transfer-print .MuiPaper-root": { boxShadow: "none !important" },
            "#stock-transfer-print .MuiTableContainer-root": {
              overflow: "visible !important",
            },
            "#stock-transfer-print .print-header-cell": {
              backgroundColor: "#1976d2 !important",
              color: "#fff !important",
              fontWeight: "700 !important",
            },
            "#stock-transfer-print .print-hide": {
              display: "none !important",
            },
          },
        }}
      />
      <Box className="no-print" sx={{ display: "flex", justifyContent: "space-between", gap: 2, flexWrap: "wrap", mb: 2 }}>
        <Typography variant="h4">{t("stock.stockTransfer", "Stock Transfer")}</Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openNewTransferDialog}>
            {t("stock.newTransfer", "New Transfer")}
          </Button>
          <Button variant="outlined" startIcon={<PrintOutlinedIcon />} onClick={printReport}>
            {t("stock.print", "Print")}
          </Button>
        </Box>
      </Box>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3000}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        sx={{
          "& .MuiPaper-root": {
            position: "relative",
            overflow: "hidden",
            borderRadius: "4px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.18)",
          },
        }}
      >
        <Alert
          onClose={() => setMessage(null)}
          severity={message?.type || "success"}
          icon={message?.type === "success" ? false : undefined}
          variant="standard"
          sx={{
            width: "100%",
            minWidth: 320,
            bgcolor: "#fff",
            color: "#757575",
            alignItems: "center",
            border: "1px solid #eee",
            boxSizing: "border-box",
            "& .MuiAlert-message": {
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: 16,
              fontWeight: 400,
            },
            "& .MuiAlert-icon": {
              padding: 0,
              marginRight: "8px",
              alignItems: "center",
            },
            "& .MuiAlert-action": {
              paddingTop: 0,
              alignItems: "flex-start",
            },
            ...(message?.type === "success"
              ? {
                  "& .MuiAlert-icon": { color: "#19c51f" },
                  "& .MuiIconButton-root": { color: "#b0b0b0" },
                }
              : {}),
          }}
        >
          {message?.type === "success" && (
            <Box
              component="span"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                bgcolor: "#18c21b",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                flex: "0 0 auto",
                fontSize: 14,
                fontWeight: 700,
                lineHeight: 1,
                mr: 0.5,
                "&::before": { content: '"✓"' },
              }}
            />
          )}
          {message?.text}
          {message?.type === "success" && (
            <Box
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                bottom: 0,
                height: 4,
                bgcolor: "#d7f5db",
                "&::after": {
                  content: '""',
                  position: "absolute",
                  inset: 0,
                  backgroundColor: "#2ecc40",
                  transformOrigin: "left center",
                  animation: "toastProgress 3s linear forwards",
                },
              }}
            />
          )}
        </Alert>
      </Snackbar>
      <Grid className="no-print" container spacing={2} sx={{ mb: 2, mt: 6 }}>
        {[
          [t("stock.totalTransfers", "Total Transfers"), summary.totalTransfers],
          [t("stock.totalSentQty", "Total Sent Qty"), summary.totalSentQuantity],
          [t("stock.totalReceivedQty", "Total Received Qty"), summary.totalReceivedQuantity],
        ].map(([label, value]) => (
          <Grid item xs={12} md={4} key={label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              <Typography variant="h5">{value}</Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
      <Grid className="no-print" container spacing={2} sx={{ mb: 2, mt: 5 }}>
        <Grid item xs={12} md={4}>
          <TextField fullWidth placeholder={t("stock.search", "Search")} value={filters.search} onChange={(e) => setFilters((p) => ({ ...p, search: e.target.value }))} InputProps={{ startAdornment: <SearchIcon /> }} />
        </Grid>
        {isAdmin && (
          <Grid item xs={12} md={2}>
            <Select fullWidth value={filters.supplier_branch} onChange={(e) => setFilters((p) => ({ ...p, supplier_branch: e.target.value }))} displayEmpty>
              <MenuItem value="">{t("stock.supplierBranch", "Source Province")}</MenuItem>
              {branches.map((b) => <MenuItem key={b.id} value={b.id}>{b.name}</MenuItem>)}
            </Select>
          </Grid>
        )}
        <Grid item xs={12} md={1}><TextField fullWidth type="date" value={filters.start_date} onChange={(e) => setFilters((p) => ({ ...p, start_date: e.target.value }))} /></Grid>
        <Grid item xs={12} md={1}><TextField fullWidth type="date" value={filters.end_date} onChange={(e) => setFilters((p) => ({ ...p, end_date: e.target.value }))} /></Grid>
      </Grid>
      <TableContainer
        id="stock-transfer-report"
        component={Paper}
        sx={{
          boxShadow: "none",
          border: "none",
          width: "100%",
          "& .MuiTablePagination-root": {
            borderTop: "none",
          },
        }}
      >
        <Table sx={{ width: "100%", "& .MuiTableCell-root": { borderBottom: "1px solid rgba(224, 224, 224, 0.6)" } }}>
          <TableHead sx={{ "& .MuiTableCell-root": { backgroundColor: "#1976d2", color: "#fff", fontWeight: 700 } }}>
            <TableRow>
              <TableCell className="print-header-cell">{t("stock.product", "Product")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.status", "Status")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.fromBranch", "From Province")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.toBranch", "To Province")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.transferQty", "Transfer Qty")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.transferDate", "Transfer Date")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.user", "User")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.totalAmount", "Total Amount")}</TableCell>
              <TableCell className="print-header-cell">{t("stock.remainedAmount", "Remained Amount")}</TableCell>
              <TableCell className="print-header-cell print-hide action-col">{t("stock.action", "Action")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>{pagedTransfers.map((group) => {
              const row = group.parent || {};
              const isExpanded = Boolean(expandedGroups[group.key]);
              const isPending = group.items.some((item) => item?.status === "pending");
              const isSourceBranch =
                String(row?.supplier_branch || "") === String(selectedBranchId || "");
              const isDestinationBranch =
                String(row?.customer_branch || "") === String(selectedBranchId || "");
              const canManageTransfer = isAdmin || isSourceBranch || isDestinationBranch;
              const hasRowActions = canManageTransfer || (isPending && isDestinationBranch);
              const parentStatus = group.items.every((item) => item?.status === "accepted")
                ? "Accepted"
                : group.items.every((item) => item?.status === "rejected")
                ? "Rejected"
                : group.items.every((item) => item?.status === "pending")
                ? "Pending"
                : "Partial";
              return (
                <React.Fragment key={group.key}>
                  <TableRow>
                    <TableCell>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                        <IconButton
                          size="small"
                          onClick={() =>
                            setExpandedGroups((prev) => ({
                              ...prev,
                              [group.key]: !prev[group.key],
                            }))
                          }
                        >
                          {isExpanded ? "▾" : "▸"}
                        </IconButton>
                        {group.displayProductName}
                      </Box>
                    </TableCell>
                    <TableCell>{parentStatus}</TableCell>
                    <TableCell>{row?.supplier_branch_details?.name || branchNameById.get(String(row?.supplier_branch)) || "-"}</TableCell>
                    <TableCell>{row?.customer_branch_details?.name || branchNameById.get(String(row?.customer_branch)) || "-"}</TableCell>
                    <TableCell>{group.totalQuantity}</TableCell>
                    <TableCell>{row?.transfer_date || "-"}</TableCell>
                    <TableCell>{row?.transfer_user_details?.username || "-"}</TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        {group.currencyBreakdown?.length
                          ? group.currencyBreakdown.map((entry) => (
                              <Typography key={`${group.key}-${entry.currencyTitle}`} variant="body2">
                                {entry.currencyTitle} {entry.amount.toFixed(2)}
                              </Typography>
                            ))
                          : <Typography variant="body2">0.00</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        {group.remainedBreakdown?.length
                          ? group.remainedBreakdown.map((entry) => (
                              <Typography key={`${group.key}-remained-${entry.currencyTitle}`} variant="body2">
                                {entry.currencyTitle} {entry.amount.toFixed(2)}
                              </Typography>
                            ))
                          : <Typography variant="body2">0.00</Typography>}
                      </Box>
                    </TableCell>
                    <TableCell className="print-hide action-col">
                      {hasRowActions && (
                        <IconButton
                          size="small"
                          onClick={(event) => openActionMenu(event, group)}
                          aria-label={t("stock.actions", "Actions")}
                        >
                          <MoreVertIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={10} sx={{ py: 0, borderBottom: isExpanded ? "1px solid rgba(224,224,224,0.6)" : 0 }}>
                      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                        <Box sx={{ py: 2, pl: 2, pr: 1 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                            Transfer Items
                          </Typography>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Product</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Quantity</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Currency</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Buy Price</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Wholesale Price</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Retail Price</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap", minWidth: 110 }}>Sale Price</TableCell>
                                <TableCell sx={{ whiteSpace: "nowrap" }}>Subtotal</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>{group.items.map((item, index) => (
                                <TableRow key={item?.id ?? `${group.key}-${index}`}>
                                  <TableCell>{item?.product_details?.name || "-"}</TableCell>
                                  <TableCell>{item?.transfer_quantity ?? "-"}</TableCell>
                                  <TableCell>{getTransferCurrencyTitle(item)}</TableCell>
                                  <TableCell>{getTransferItemSourcePrices(item).buy_price || "-"}</TableCell>
                                  <TableCell>{getTransferItemSourcePrices(item).wholesale_price || "-"}</TableCell>
                                  <TableCell>{getTransferItemSourcePrices(item).retail_price || "-"}</TableCell>
                                  <TableCell sx={{ whiteSpace: "nowrap", minWidth: 110 }}>{getTransferItemSalePrice(item).toFixed(2)}</TableCell>
                                  <TableCell sx={{ whiteSpace: "nowrap" }}>{((Number(item?.transfer_quantity) || 0) * getTransferItemSalePrice(item)).toFixed(2)}</TableCell>
                                </TableRow>
                              ))}</TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              );
            })}</TableBody>
        </Table>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 20, 30, 50, 100, { label: "All", value: -1 }]}
          labelRowsPerPage="Rows per page:"
          count={groupedTransfers.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            const nextRowsPerPage = Number(e.target.value);
            setRowsPerPage(nextRowsPerPage);
            setPage(0);
          }}
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            borderTop: "none",
            overflow: "visible",
            "& .MuiTablePagination-toolbar": {
              justifyContent: "center",
              flexWrap: "nowrap",
              gap: 0.75,
              minHeight: 48,
            },
            "& .MuiTablePagination-spacer": {
              display: "none",
            },
            "& .MuiTablePagination-selectLabel": {
              margin: 0,
              fontSize: 15,
              color: "#111",
              whiteSpace: "nowrap",
            },
            "& .MuiTablePagination-select": {
              fontSize: 15,
              marginRight: 0,
            },
            "& .MuiTablePagination-selectIcon": {
              top: "calc(50% - 10px)",
            },
            "& .MuiTablePagination-displayedRows": {
              margin: 0,
              display: "none",
            },
            "& .MuiTablePagination-actions": {
              marginLeft: 0,
              paddingLeft: 0,
            },
          }}
        />
      </TableContainer>

      <Menu
        anchorEl={actionMenuAnchorEl}
        open={Boolean(actionMenuAnchorEl)}
        onClose={closeActionMenu}
      >
        {actionMenuGroup && (() => {
          const row = actionMenuGroup.parent || {};
          const isPending = actionMenuGroup.items.some((item) => item?.status === "pending");
          const isSourceBranch =
            String(row?.supplier_branch || "") === String(selectedBranchId || "");
          const isDestinationBranch =
            String(row?.customer_branch || "") === String(selectedBranchId || "");
          const canManageTransfer = isAdmin || isSourceBranch || isDestinationBranch;
          const canViewTransfer = isAdmin || isSourceBranch;
          const canEditTransfer = canManageTransfer;
          const canDeleteTransfer = canManageTransfer;
          return (
            <>
              {canViewTransfer && (
                <MenuItem
                  onClick={() => {
                    setSelectedTransfer(row);
                    setOpenDetails(true);
                    closeActionMenu();
                  }}
                  >
                  <VisibilityIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t("stock.viewTransfer", "View transfer")}
                </MenuItem>
              )}
              {canViewTransfer && (
                <MenuItem
                  onClick={() => {
                    printTransferDetails(actionMenuGroup);
                    closeActionMenu();
                  }}
                >
                  <PrintOutlinedIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t("stock.print", "Print")}
                </MenuItem>
              )}
              {canEditTransfer && (
                <MenuItem
                  onClick={() => {
                    openEditTransferDialog(actionMenuGroup);
                    closeActionMenu();
                  }}
                >
                  <EditIcon fontSize="small" style={{ marginRight: 8 }} />
                  {t("stock.edit", "Edit")}
                </MenuItem>
              )}
              {canDeleteTransfer && (
                <MenuItem
                  onClick={() => {
                    deleteTransferGroup(actionMenuGroup);
                    closeActionMenu();
                  }}
                  disabled={deletingTransferGroupKey === actionMenuGroup.key}
                >
                  <DeleteOutlineIcon fontSize="small" style={{ marginRight: 8 }} />
                  {deletingTransferGroupKey === actionMenuGroup.key
                    ? t("stock.deleting", "Deleting...")
                    : t("stock.delete", "Delete")}
                </MenuItem>
              )}
              {isPending && (isAdmin || isDestinationBranch) && (
                <>
                  <MenuItem
                    onClick={() => {
                      acceptTransferGroup(actionMenuGroup);
                      closeActionMenu();
                    }}
                  >
                    {t("stock.acceptTransfer", "Accept Transfer")}
                  </MenuItem>
                  <MenuItem
                    onClick={() => {
                      rejectTransferGroup(actionMenuGroup);
                      closeActionMenu();
                    }}
                  >
                    {t("stock.rejectTransfer", "Reject Transfer")}
                  </MenuItem>
                </>
              )}
            </>
          );
        })()}
      </Menu>

      <Dialog
        open={openTransferConfirm}
        onClose={() => {
          setOpenTransferConfirm(false);
          setPendingTransfer(null);
          setSelectedTransferGroup(null);
          setSelectedTransfer(null);
          setDialogMode("view");
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {dialogMode === "accept"
            ? t("stock.confirmAcceptance", "Confirm Transfer Acceptance")
            : dialogMode === "reject"
            ? t("stock.confirmRejection", "Confirm Transfer Rejection")
            : t("stock.confirmTransfer", "Confirm Stock Transfer")}
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransfer && (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight="bold">{t("stock.transferDetails", "Transfer Details")}:</Typography>
              <Typography>
                {t("stock.transferId", "Transfer ID")}: {selectedTransferGroup ? selectedTransferGroup.key : selectedTransfer.transfer_id}
              </Typography>
              <Typography>
                {t("stock.product", "Product")}: {selectedTransferGroup ? selectedTransferGroup.displayProductName : selectedTransfer.product_details?.name || "-"}
              </Typography>
              {dialogMode === "accept" && (
                <>
                  <Typography>{t("stock.fromBranch", "From Province")}: {selectedTransfer.supplier_branch_details?.name || branchNameById.get(String(selectedTransfer.supplier_branch)) || "-"}</Typography>
                  <Typography>{t("stock.toBranch", "To Province")}: {selectedTransfer.customer_branch_details?.name || branchNameById.get(String(selectedTransfer.customer_branch)) || "-"}</Typography>
                </>
              )}
              <Typography>
                {t("stock.transferQty", "Quantity")}: {selectedTransferGroup ? selectedTransferGroup.totalQuantity : selectedTransfer.transfer_quantity}
              </Typography>
              <Typography>
                {t("stock.status", "Status")}: {selectedTransferGroup ? (
                  selectedTransferGroup.items.every((item) => item?.status === "accepted")
                    ? "Accepted"
                    : selectedTransferGroup.items.every((item) => item?.status === "rejected")
                    ? "Rejected"
                    : "Pending"
                ) : selectedTransfer.status_display || selectedTransfer.status || "-"}
              </Typography>
              <Typography>{t("stock.created", "Created")}: {selectedTransfer.transfer_date}</Typography>
              {selectedTransferGroup && (
                <Typography>
                  Pending Items: {(selectedTransferGroup.items || []).filter((item) => String(item?.status || "").toLowerCase() === "pending").length}
                </Typography>
              )}
              {selectedTransfer.status === "pending" && dialogMode === "accept" && (
                <Alert severity="info">
                  {t("stock.acceptHelp", "Accepting this transfer will move stock to the destination province and mark it as accepted.")}
                </Alert>
              )}
              {selectedTransfer.status === "pending" && dialogMode === "reject" && (
                <Alert severity="warning">
                  {t("stock.rejectHelp", "Rejecting this transfer will mark it as rejected and keep the stock in the source province.")}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenTransferConfirm(false);
              setPendingTransfer(null);
              setSelectedTransferGroup(null);
              setSelectedTransfer(null);
              setDialogMode("view");
            }}
          >
            {t("stock.cancel", "Cancel")}
          </Button>
          {dialogMode === "accept" ? (
            <Button onClick={submitTransfer} variant="contained">
              {t("stock.acceptTransfer", "Accept Transfer")}
            </Button>
          ) : dialogMode === "reject" ? (
            <Button onClick={submitTransfer} variant="contained" color="error">
              {t("stock.rejectTransfer", "Reject Transfer")}
            </Button>
          ) : null}
        </DialogActions>
      </Dialog>

      <Dialog open={openPayment} onClose={() => setOpenPayment(false)} fullWidth maxWidth="sm">
        <DialogTitle>{t("stock.recordPayment", "Record Payment")}</DialogTitle>
        <DialogContent dividers>
          {paymentData && (
            <Box sx={{ display: "grid", gap: 2, pt: 1 }}>
              <TextField label={t("stock.product", "Product")} value={paymentData.product_details?.name || "-"} fullWidth InputProps={{ readOnly: true }} />
              <TextField label={t("stock.fromBranch", "From Province")} value={paymentData.supplier_branch_details?.name || branchNameById.get(String(paymentData.supplier_branch)) || "-"} fullWidth InputProps={{ readOnly: true }} />
              <TextField label={t("stock.toBranch", "To Province")} value={paymentData.customer_branch_details?.name || branchNameById.get(String(paymentData.customer_branch)) || "-"} fullWidth InputProps={{ readOnly: true }} />
              <TextField label={t("stock.transferQty", "Transfer Qty")} value={paymentData.transfer_quantity || "-"} fullWidth InputProps={{ readOnly: true }} />
              <TextField
                label={t("stock.totalAmount", "Total Amount")}
                value={Number.isFinite(paymentTotalAmount) ? paymentTotalAmount.toLocaleString() : "-"}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                select
                label={t("stock.account", "Account")}
                value={paymentAccountId}
                onChange={(e) => setPaymentAccountId(e.target.value)}
                fullWidth
              >
                {accounts.map((account) => (
                  <MenuItem key={account.id} value={String(account.id)}>
                    {account.name} ({account.currency_title || "-"})
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label={t("stock.paymentAmount", "Payment Amount")}
                type="number"
                inputProps={{ min: 0.01, step: 0.01 }}
                value={paymentAmount}
                onChange={(e) => {
                  const nextValue = e.target.value;
                  setPaymentAmount(nextValue);
                }}
                fullWidth
              />
              <TextField
                label={t("stock.remainedAmount", "Remained Amount")}
                value={Number.isFinite(paymentRemainingAmount) ? String(paymentRemainingAmount.toLocaleString()) : "0"}
                fullWidth
                InputProps={{ readOnly: true }}
              />
              <TextField
                label={t("stock.date", "Date")}
                type="date"
                InputLabelProps={{ shrink: true }}
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                fullWidth
              />
              <TextField
                label={t("stock.description", "Description")}
                value={paymentDescription}
                onChange={(e) => setPaymentDescription(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenPayment(false)} disabled={paymentSubmitting}>
            {t("stock.cancel", "Cancel")}
          </Button>
          <Button
            onClick={submitPayment}
            variant="contained"
          disabled={
            paymentSubmitting ||
            !paymentAccountId ||
            !paymentAmount ||
            Number(paymentAmount) <= 0 ||
            Number(paymentAmount) > (paymentOutstandingAmount > 0 ? paymentOutstandingAmount : paymentTotalAmount)
          }
          >
            {paymentSubmitting ? t("stock.saving", "Saving...") : t("stock.addPayment", "Add Payment")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openForm}
        onClose={() => setOpenForm(false)}
        fullWidth
        scroll="paper"
        maxWidth={false}
        PaperProps={{
          sx: {
            width: { xs: "95vw", md: "82vw" },
            maxWidth: "780px",
            maxHeight: "92vh",
            display: "flex",
          },
        }}
      >
        <DialogTitle>
          {dialogMode === "edit"
            ? t("stock.editTransferTitle", "Edit Stock Transfer")
            : t("stock.newTransferTitle", "New Stock Transfer")}
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            pt: 2,
            display: "grid",
            gap: 2,
            overflowY: "auto",
          }}
        >
          {transferItems.map((item, index) => {
            const isEditMode = dialogMode === "edit";
            const itemStock = getAvailableQuantityForItem(item, index, isEditMode);
            const productOptions = isEditMode ? getAllProductOptionsForItem(item) : availableProducts;
            return (
              <Box
                key={index}
                sx={{
                  border: "1px solid #e0e0e0",
                  borderRadius: 1,
                  p: 1.5,
                  position: "relative",
                  bgcolor: "#fff",
                }}
              >
                <IconButton
                  size="small"
                  onClick={() =>
                    setTransferItems((prev) =>
                      prev.length > 1 ? prev.filter((_, rowIndex) => rowIndex !== index) : prev
                    )
                  }
                  sx={{ position: "absolute", top: 8, right: 8 }}
                >
                  <RemoveCircleOutlineIcon sx={{ color: "#e53935" }} />
                </IconButton>
                <Box sx={{ display: "grid", gap: 1.5, pr: 4 }}>
                  {isEditMode ? (
                      <Select
                        value={item.product}
                        renderValue={() =>
                          productOptions.find((p) => String(p.id) === String(item.product))?.name ||
                          getTransferItemDisplayName(item)
                        }
                        onChange={(e) => {
                          const nextProductId = e.target.value;
                          setTransferItems((prev) =>
                            prev.map((row, rowIndex) =>
                              rowIndex === index
                                ? {
                                    ...row,
                                    product: nextProductId,
                                    currency: getDefaultCurrencyIdForProduct(nextProductId),
                                    ...getItemPriceValues(nextProductId),
                                    buy_price_touched: false,
                                    wholesale_price_touched: false,
                                    retail_price_touched: false,
                                    sale_price_touched: false,
                                }
                              : row
                          )
                        );
                      }}
                      displayEmpty
                      fullWidth
                    >
                      <MenuItem value="">Select Product *</MenuItem>
                      {productOptions.map((p) => (
                        <MenuItem key={p.id} value={p.id}>
                          {p.name}
                        </MenuItem>
                      ))}
                    </Select>
                  ) : (
                    <Autocomplete
                      options={productOptions}
                      value={productOptions.find((p) => String(p.id) === String(item.product)) || null}
                      onChange={(_, nextValue) => {
                        const nextProductId = nextValue?.id || "";
                        setTransferItems((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index
                              ? {
                                  ...row,
                                  product: nextProductId,
                                  currency: getDefaultCurrencyIdForProduct(nextProductId),
                                  ...getItemPriceValues(nextProductId),
                                  buy_price_touched: false,
                                  wholesale_price_touched: false,
                                  retail_price_touched: false,
                                  sale_price_touched: false,
                                }
                              : row
                          )
                        );
                      }}
                      getOptionLabel={(option) => option?.name || ""}
                      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Product *"
                          placeholder="Search product"
                        />
                      )}
                      fullWidth
                    />
                  )}
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: {
                        xs: "1fr",
                        md: isEditMode ? "1fr 1fr" : "1fr 1fr 1fr",
                      },
                    }}
                  >
                    <TextField
                      value={itemStock}
                      label="Available Quantity"
                      placeholder={item.product ? "0" : "Select Product"}
                      fullWidth
                      InputProps={{ readOnly: true }}
                    />
                    <TextField
                      label="Send Qty *"
                      type="number"
                      value={item.transfer_quantity}
                      onChange={(e) =>
                        setTransferItems((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, transfer_quantity: e.target.value } : row
                          )
                        )
                      }
                      fullWidth
                      inputProps={{ min: 1, max: itemStock || undefined }}
                    />
                    <TextField
                      select
                      label={t("stock.currency", "Currency")}
                      value={getItemCurrencyId(item)}
                      onChange={(e) =>
                        setTransferItems((prev) =>
                          prev.map((row, rowIndex) =>
                            rowIndex === index ? { ...row, currency: e.target.value } : row
                          )
                        )
                      }
                      fullWidth
                      disabled={currencyLoading || !currencies.length}
                    >
                      <MenuItem value="">{t("stock.selectCurrency", "Select Currency")}</MenuItem>
                      {currencies.map((currency) => (
                        <MenuItem key={currency.id} value={String(currency.id)}>
                          {currency.title}
                        </MenuItem>
                      ))}
                    </TextField>
                  </Box>
                  <Box
                    sx={{
                      display: "grid",
                      gap: 1,
                      gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                    }}
                  >
                    <TextField
                      label={`${t("stock.buyPrice", "Buy Price")}${getItemCurrencyTitle(item) ? ` (${getItemCurrencyTitle(item)})` : ""}`}
                      type="number"
                      value={getTransferItemPriceValue(item, "buy_price")}
                      onChange={(e) => handleTransferItemChange(index, "buy_price", e.target.value)}
                      fullWidth
                      inputProps={{ step: "0.01" }}
                    />
                    <TextField
                      label={`${t("stock.wholesalePrice", "Wholesale Price")}${getItemCurrencyTitle(item) ? ` (${getItemCurrencyTitle(item)})` : ""}`}
                      type="number"
                      value={getTransferItemPriceValue(item, "wholesale_price")}
                      onChange={(e) => handleTransferItemChange(index, "wholesale_price", e.target.value)}
                      fullWidth
                      inputProps={{ step: "0.01" }}
                    />
                    <TextField
                      label={`${t("stock.retailPrice", "Retail Price")}${getItemCurrencyTitle(item) ? ` (${getItemCurrencyTitle(item)})` : ""}`}
                      type="number"
                      value={getTransferItemPriceValue(item, "retail_price")}
                      onChange={(e) => handleTransferItemChange(index, "retail_price", e.target.value)}
                      fullWidth
                      inputProps={{ step: "0.01" }}
                    />
                </Box>
              </Box>
            </Box>
          );
        })}
          <Button
            variant="outlined"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() =>
              setTransferItems((prev) => [
                ...prev,
                createEmptyTransferItem(),
              ])
            }
          >
            {t("stock.addItem", "Add Item")}
          </Button>
          {isSuperAdmin ? (
            <TextField
              select
              value={form.supplier_branch}
              onChange={(e) => setForm((p) => ({ ...p, supplier_branch: e.target.value }))}
              fullWidth
              label={supplierBranchLabel}
            >
              <MenuItem value="">{t("stock.selectFromBranch", "Select From Province")}</MenuItem>
              {branches.map((b) => (
                <MenuItem key={b.id} value={String(b.id)}>
                  {b.name}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <TextField
              value={safeSupplierBranch ? branchNameById.get(String(safeSupplierBranch)) || "" : ""}
              label={supplierBranchLabel}
              fullWidth
              InputProps={{ readOnly: true }}
            />
          )}
          <TextField
            select
            value={safeCustomerBranch}
            onChange={(e) => setForm((p) => ({ ...p, customer_branch: e.target.value }))}
            fullWidth
            label={customerBranchLabel}
          >
            <MenuItem value="">{customerBranchLabel}</MenuItem>
            {customerBranchOptions.map((b) => (
              <MenuItem key={b.id} value={b.id}>
                {b.name}
              </MenuItem>
            ))}
          </TextField>
          <TextField value={form.transfer_date} onChange={(e) => setForm((p) => ({ ...p, transfer_date: e.target.value }))} label={t("stock.transferDate", "Transfer Date")} type="date" fullWidth InputLabelProps={{ shrink: true }} />
          <TextField value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} label={t("stock.notes", "Notes")} fullWidth multiline minRows={3} />
          {showPaymentBranch && (
            <Box sx={{ display: "grid", gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ mt: 1, fontWeight: 700 }}>
                {t("stock.paymentSummary", "Payment Summary")}
              </Typography>
              <Box sx={{ display: "grid", gap: 0.5, px: 0.5 }}>
                {hasMixedTransferCurrencies ? (
                  <>
                    <Typography variant="body2" color="warning.main">
                      {t(
                        "stock.mixedCurrenciesNotice",
                        "This transfer contains more than one currency, so totals are shown per currency."
                      )}
                    </Typography>
                    {transferCurrencyBreakdown.map((row) => (
                      <Typography key={row.currencyId} variant="body1">
                        {t("stock.subtotal", "Subtotal")}: {row.subtotal.toFixed(2)} {row.currencyTitle}
                      </Typography>
                    ))}
                  </>
                ) : (
                  <>
                    <Typography variant="body1">
                      {t("stock.subtotal", "Subtotal")}: {transferSubtotalAmount.toFixed(2)} {transferCurrencyTitle}
                    </Typography>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {t("stock.totalAmount", "Total Amount")}: {transferSubtotalAmount.toFixed(2)} {transferCurrencyTitle}
                    </Typography>
                    <Typography variant="body1">
                      {t("stock.remainedAmount", "Remained Amount")}: {transferSubtotalAmount.toFixed(2)} {transferCurrencyTitle}
                    </Typography>
                  </>
                )}
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            position: "sticky",
            bottom: 0,
            zIndex: 1,
            bgcolor: "background.paper",
            borderTop: "1px solid",
            borderColor: "divider",
            px: 2,
            py: 1.5,
          }}
        >
          <FormControlLabel
            sx={{ mr: "auto", ml: 1 }}
            control={
              <Checkbox
                checked={showPaymentBranch}
                onChange={(e) => setShowPaymentBranch(e.target.checked)}
              />
            }
            label={t("stock.payment", "Payment")}
          />
          <Button onClick={() => setOpenForm(false)}>{t("stock.cancel", "Cancel")}</Button>
          <Button
            onClick={submitTransfer}
            variant="contained"
            disabled={!safeSupplierBranch || !safeCustomerBranch || !transferItems.some((item) => item.product && item.transfer_quantity && Number(item.transfer_quantity) > 0)}
          >
            {dialogMode === "edit"
              ? t("stock.saveChanges", "Save Edit")
              : t("stock.createTransfer", "Create Transfer")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDetails} onClose={() => setOpenDetails(false)} fullWidth maxWidth="sm">
        <DialogTitle>Transfer Details</DialogTitle>
        <DialogContent dividers>
          {selectedTransfer && (
            <Box sx={{ display: "grid", gap: 1 }}>
              <Typography>ID: {selectedTransfer.transfer_id}</Typography>
              <Typography>Product: {selectedTransfer.product_details?.name}</Typography>
              <Typography>From Province: {selectedTransfer.supplier_branch_details?.name || branchNameById.get(String(selectedTransfer.supplier_branch)) || "-"}</Typography>
              <Typography>To Province: {selectedTransfer.customer_branch_details?.name || branchNameById.get(String(selectedTransfer.customer_branch)) || "-"}</Typography>
              <Typography>Quantity: {selectedTransfer.transfer_quantity}</Typography>
              <Typography>Old Qty: {selectedTransfer.old_quantity}</Typography>
              <Typography>New Qty: {selectedTransfer.new_quantity}</Typography>
              <Typography>User: {selectedTransfer.transfer_user_details?.username}</Typography>
              <Typography>Date: {selectedTransfer.transfer_date}</Typography>
              <Typography>Notes: {selectedTransfer.notes || "-"}</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            variant="outlined"
            startIcon={<PrintOutlinedIcon />}
            onClick={() => printTransferDetails(selectedTransfer)}
          >
            Print
          </Button>
          <Button onClick={() => setOpenDetails(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StockTransferIndex;






